import { PageShell, Block } from "./PageShell";

const DELETE_EMAIL = "support@ourheardcommunity.com";

const inAppSteps = [
  "Open the HEARD app and go to your Profile.",
  "Tap Settings, then Account.",
  "Select \u201cDelete Account\u201d and confirm your choice.",
  "Your account and associated personal data will be permanently deleted within 30 days.",
];

const deletedData = [
  "Your account and login credentials",
  "Your private username and profile details",
  "Your posts, comments, reactions, and messages",
  "Your check-ins, journal entries, and saved content",
  "Device and contact details you provided at signup (e.g., email)",
];

const retainedData = [
  "Records we are legally required to keep (e.g., for fraud prevention, safety, or law-enforcement requests) — retained only as long as required by law.",
  "Anonymized or aggregated data that can no longer identify you — may be retained to understand overall usage.",
  "Backup copies — securely deleted within our routine backup cycle (no more than 90 days).",
];

const List = ({ items, ordered = false }) => {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={`${ordered ? "list-decimal" : "list-disc"} space-y-1.5 pl-5 marker:text-sage`}>
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </Tag>
  );
};

export default function DeleteAccount() {
  return (
    <PageShell
      title="Account Deletion"
      intro="You can delete your HEARD account and personal data at any time. This page explains how to request deletion, what data is removed, and what may be retained — in line with Google Play requirements."
    >
      <Block heading="How to delete your account">
        <p>You can delete your account and its data directly from within the app:</p>
        <List items={inAppSteps} ordered />
        <p>
          Prefer email? You can also request account and data deletion by writing to{" "}
          <a className="text-sage-deep underline" href={`mailto:${DELETE_EMAIL}`} data-testid="delete-account-email">
            {DELETE_EMAIL}
          </a>{" "}
          from the email address associated with your account. We will verify your request and process it within 30 days.
        </p>
        <p className="text-sm text-ink/60">
          If you only joined the pre-launch waitlist, email us at the address above and we will remove your details from
          our beta list.
        </p>
      </Block>

      <Block heading="What data is deleted">
        <p>When your account is deleted, we permanently remove:</p>
        <List items={deletedData} />
      </Block>

      <Block heading="What data may be retained (and for how long)">
        <p>In limited cases, some data may be retained after account deletion:</p>
        <List items={retainedData} />
        <p>
          Retained data is never sold and is only kept for the minimum period necessary. Once retention obligations
          expire, the data is securely deleted.
        </p>
      </Block>

      <Block heading="Deletion timeline">
        <p>
          Account deletion requests are completed within <span className="font-medium text-sage-deep">30 days</span>.
          Backup copies are purged within our routine backup cycle (no more than 90 days).
        </p>
      </Block>

      <Block heading="Contact">
        <p>
          Questions about deleting your account or data? Email{" "}
          <a className="text-sage-deep underline" href={`mailto:${DELETE_EMAIL}`}>
            {DELETE_EMAIL}
          </a>
          .
        </p>
      </Block>
    </PageShell>
  );
}
